import { FeatureFlagKey, FeatureFlagMap } from "@swivl-mono/types";

import { Env } from "../Env/Env.Helper";

/**
 * The Outbound Gate's decision (ENG-3373): the ONE question every text, call, email, push
 * and FMS write asks before it can leave Swivl.
 *
 *   org's stream flag off               → simulated (`flag_off`)
 *   flag on, production                 → real      (`flag_on`)
 *   flag on, elsewhere, recipient listed → real     (`listed`)
 *   flag on, elsewhere, not listed       → simulated (`not_listed`)
 *
 * The **flag** is the org-level control — one feature flag per stream (texts, calls,
 * emails, FMS updates, staff push), key present = on. The **allowlist** is the fine-grained
 * "who", consulted only outside production. Consent is a separate, later question the
 * wrappers ask (decision 7). No env var decides anything. Until the loader has delivered an
 * allowlist snapshot every non-production decision is `simulated` (fail closed).
 *
 * Why here and not in each feature: on 2026-09-21/22 three gates with three meanings let
 * eleven test notes reach a real customer's StorEdge ledger. Callers do not decide; the four
 * outbound wrappers call this and honour the answer.
 */

/**
 * What is being sent, which also says what the recipient is:
 *   text / call → an E.164 phone;  email → an address;  push → a member id;
 *   fmsNote / fmsPayment → the Swivl tenant id whose FMS record is written;
 *   fmsLead → the lead's phone (a lead has no tenant yet; a tester's own number is what gets allowed).
 */
export type OutboundKind = "text" | "call" | "email" | "push" | "fmsNote" | "fmsLead" | "fmsPayment";

export type OutboundResult = "real" | "simulated";

export type OutboundReason =
  /** The org's flag for this stream is not set. */
  | "flag_off"
  /** Production and the flag is on — the flag alone decides there. */
  | "flag_on"
  /** A system send (no org — Bull alerts, error emails, the 2FA number) in production. Decision 12. */
  | "system"
  /** Outside production: the recipient itself is on the allowlist. */
  | "listed"
  | "not_listed"
  /** No snapshot has arrived in this process yet — fail closed. */
  | "allowlist_not_loaded"
  /** Nothing to check: the caller had no phone / email / member / tenant. */
  | "no_recipient"
  /** The sequence runs in Simulated mode (plan decision 1): every delivery is withheld, allowed or not. */
  | "sequence_simulated";

export interface OutboundDecision {
  result: OutboundResult;
  reason: OutboundReason;
  kind: OutboundKind;
  /** The normalized recipient the decision was made on (null when there was none). */
  recipient: string | null;
  /** The stream flag the decision read (null for a system send, which has no org). */
  flag: FeatureFlagKey | null;
  /** Decision 12: the send was marked as Swivl's own, not an org's. Shown on every record of it. */
  system: boolean;
}

export interface OutboundDecideOptions {
  /**
   * Decision 12: a send that belongs to Swivl, not to an org — Bull job alerts, error emails, the
   * shared 2FA number's codes. There is no org flag to read, so the mark is what lets it pass in
   * production; outside production the allowlist still applies. Never set this on a customer send.
   */
  system?: boolean;
  /**
   * Plan decision 1: a sequence in Simulated mode is a full dry run — every delivery is built and
   * recorded and none leaves, even for an allowed recipient. Checked before anything else.
   */
  simulate?: boolean;
}

/** The org as the gate needs it: its id and its feature flags. */
export interface OutboundOrg {
  id: number;
  featureFlags?: FeatureFlagMap | null;
}

/** What the loader hands in from `outbound_allowlist`, one set per kind. */
export interface OutboundAllowlistSets {
  /** E.164 numbers — texts and calls. */
  phones: Iterable<string>;
  /** Compared lower-cased. */
  emails: Iterable<string>;
  /** Push. */
  memberIds: Iterable<number>;
  /** Swivl tenant ids — FMS notes and payments. */
  tenantIds: Iterable<number>;
}

/** Kinds whose recipient is a Swivl tenant id. */
const TENANT_KINDS: ReadonlySet<OutboundKind> = new Set<OutboundKind>(["fmsNote", "fmsPayment"]);
/** Kinds whose recipient is an E.164 phone. */
const PHONE_KINDS: ReadonlySet<OutboundKind> = new Set<OutboundKind>(["text", "call", "fmsLead"]);

/** Which org flag governs each kind. */
export const OUTBOUND_STREAM_FLAG: Record<OutboundKind, FeatureFlagKey> = {
  text: FeatureFlagKey.OutboundTexts,
  call: FeatureFlagKey.OutboundCalls,
  email: FeatureFlagKey.OutboundEmails,
  push: FeatureFlagKey.OutboundStaffPush,
  fmsNote: FeatureFlagKey.OutboundFms,
  fmsLead: FeatureFlagKey.OutboundFms,
  fmsPayment: FeatureFlagKey.OutboundFms
};

let phones = new Set<string>();
let emails = new Set<string>();
let memberIds = new Set<number>();
let tenantIds = new Set<number>();
let loaded = false;

/** Replace the in-process sets. Called by the server-helpers loader after every read; tests call it directly. */
export function setOutboundDecisionAllowlist(sets: OutboundAllowlistSets): void {
  phones = new Set(Array.from(sets.phones, (p) => p.trim()).filter((p) => p.length > 0));
  emails = new Set(Array.from(sets.emails, (e) => e.trim().toLowerCase()).filter((e) => e.length > 0));
  memberIds = new Set(Array.from(sets.memberIds).filter((n) => Number.isFinite(n)));
  tenantIds = new Set(Array.from(sets.tenantIds).filter((n) => Number.isFinite(n)));
  loaded = true;
}

/** Whether this process has received at least one snapshot. Surfaced on the admin page. */
export function isOutboundDecisionAllowlistLoaded(): boolean {
  return loaded;
}

/** Test-only: back to "not loaded". Production code must not call this. */
export function __resetOutboundDecisionForTests(): void {
  phones = new Set();
  emails = new Set();
  memberIds = new Set();
  tenantIds = new Set();
  loaded = false;
}

/** The recipient as the allowlist stores it, or null when there is nothing usable to check. */
export function normalizeOutboundRecipient(kind: OutboundKind, recipient: string | number | null | undefined): string | null {
  if (recipient === null || recipient === undefined) return null;
  if (kind === "push" || TENANT_KINDS.has(kind)) {
    const id = typeof recipient === "number" ? recipient : parseInt(String(recipient).trim(), 10);
    return Number.isFinite(id) ? String(id) : null;
  }
  const value = String(recipient).trim();
  if (!value) return null;
  return kind === "email" ? value.toLowerCase() : value;
}

/** Key present = on. The account-kind level a flag carries has no meaning for a send. */
export function isOutboundStreamOn(org: OutboundOrg, kind: OutboundKind): boolean {
  return Boolean(org.featureFlags?.[OUTBOUND_STREAM_FLAG[kind]]);
}

function isListed(kind: OutboundKind, normalized: string): boolean {
  if (kind === "push") return memberIds.has(Number(normalized));
  if (TENANT_KINDS.has(kind)) return tenantIds.has(Number(normalized));
  if (kind === "email") return emails.has(normalized);
  return PHONE_KINDS.has(kind) && phones.has(normalized);
}

/**
 * The decision. Pure and synchronous: the sets are in memory and the org's flags are handed
 * in, so every wrapper can call it on the hot path.
 */
export function decideOutbound(
  kind: OutboundKind,
  org: OutboundOrg | null,
  recipient: string | number | null | undefined,
  options: OutboundDecideOptions = {}
): OutboundDecision {
  const system = options.system === true;
  const normalized = normalizeOutboundRecipient(kind, recipient);
  const base = { kind, recipient: normalized, flag: system ? null : OUTBOUND_STREAM_FLAG[kind], system };
  if (options.simulate) return { ...base, result: "simulated", reason: "sequence_simulated" };
  if (!system) {
    if (!org || !isOutboundStreamOn(org, kind)) return { ...base, result: "simulated", reason: "flag_off" };
  }
  if (normalized === null) return { ...base, result: "simulated", reason: "no_recipient" };
  if (Env.isProductionContext) return { ...base, result: "real", reason: system ? "system" : "flag_on" };
  if (!loaded) return { ...base, result: "simulated", reason: "allowlist_not_loaded" };
  if (isListed(kind, normalized)) return { ...base, result: "real", reason: "listed" };
  return { ...base, result: "simulated", reason: "not_listed" };
}
